import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Update as UpdateIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Campaign as AdsIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import VersionManagement from './VersionManagement';
import AdConfiguration from './AdConfiguration';
import AppConfiguration from './AppConfiguration';
import LanguageManagement from './LanguageManagement';
import StringTranslationManagement from './StringTranslationManagement';
import CreditConfiguration from './CreditConfiguration';
import FeatureConfiguration from './FeatureConfiguration';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const settingsTabs = [
    {
      label: 'Version Management',
      icon: <UpdateIcon />,
      component: <VersionManagement />
    },
    {
      label: 'Ad Configuration',
      icon: <AdsIcon />,
      component: <AdConfiguration />
    },
    {
      label: 'General Settings',
      icon: <SettingsIcon />,
      component: <AppConfiguration />
    },
    {
      label: 'Credit Configuration',
      icon: <CreditCardIcon />,
      component: <CreditConfiguration />
    },
    {
      label: 'Feature Configuration',
      icon: <SettingsIcon />,
      component: <FeatureConfiguration />
    },
    {
      label: 'Languages',
      icon: <LanguageIcon />,
      component: <LanguageManagement />
    },
    {
      label: 'Translations',
      icon: <TranslateIcon />,
      component: <StringTranslationManagement />
    },
    {
      label: 'Security',
      icon: <SecurityIcon />,
      component: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Security Settings
          </Typography>
          <Typography color="text.secondary">
            Security and authentication settings will be available here.
          </Typography>
        </Box>
      )
    },
    {
      label: 'Notifications',
      icon: <NotificationsIcon />,
      component: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Notification Settings
          </Typography>
          <Typography color="text.secondary">
            Notification preferences will be available here.
          </Typography>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>
      
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="settings tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {settingsTabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  id={`settings-tab-${index}`}
                  aria-controls={`settings-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Box>
          
          {settingsTabs.map((tab, index) => (
            <TabPanel key={index} value={activeTab} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings;
